import { BatteryRange } from '../models/asset.model';
import {
  BatteryChargingStatus,
  BatterySocStatus,
  ChargerType,
} from '../models/asset-report.model';

export const MIN_VALID_SOC_VALUE = 0;
export const MAX_VALID_SOC_VALUE = 100;

//TODO: Get thresholds from endpoint
export const BATTERY_SOC_RANGES_OLD: {
  [k in BatterySocStatus]: {
    start?: number;
    end?: number;
    inclusiveStart?: boolean;
    inclusiveEnd?: boolean;
  };
} = {
  [BatterySocStatus.CRITICAL_CHARGE]: {
    end: 15,
  },
  [BatterySocStatus.LOW_CHARGE]: {
    start: 15,
    end: 25,
    inclusiveStart: true,
    inclusiveEnd: true,
  },
  [BatterySocStatus.MEDIUM_CHARGE]: {
    start: 25,
    end: 80,
    inclusiveEnd: true,
  },
  [BatterySocStatus.HIGH_CHARGE]: {
    start: 80,
  },
};

/**
 * Rounding rules to ensure rounding does not end up conflicting with SOC diagnostic flag logic applied in the back-end; specifically:
 * * Values under 15 must not round to 15 (critical)
 * * Values over 25 must not round to 25 (good)
 * * Values between 15 and 25 may round to 15 or 25 (impaired)
 */
export const BATTERY_RANGE_VALUE_MAPPERS: {
  [k in BatteryRange]: (value: number) => number;
} = {
  [BatteryRange.LOW]: (value: number) => Math.floor(value),
  [BatteryRange.MEDIUM]: (value: number) => Math.round(value),
  [BatteryRange.HIGH]: (value: number) => Math.ceil(value),
  [BatteryRange.UNKNOWN]: (value: number) => Math.round(value),
};

/**
 * Checks charging status conditions for displaying target soc related fields
 */
export const isTargetSocDisplayValid = (
  chargingStatus?: BatteryChargingStatus
) => {
  return (
    chargingStatus &&
    [
      BatteryChargingStatus.CHARGING,
      BatteryChargingStatus.IDLE,
      BatteryChargingStatus.COMPLETED,
    ].includes(chargingStatus)
  );
};

/**
 * Checks charging status conditions for displaying estimated completion time
 */
export const isEstCompletionTimeDisplayValid = (
  chargingStatus?: BatteryChargingStatus
) => {
  return (
    chargingStatus &&
    [BatteryChargingStatus.CHARGING, BatteryChargingStatus.IDLE].includes(
      chargingStatus
    )
  );
};

/**
 * Checks charging status conditions for displaying charger type
 */
export const isChargerTypeDisplayValid = (
  chargerType?: ChargerType,
  chargingStatus?: BatteryChargingStatus
) => {
  return (
    (chargerType ?? ChargerType.UNKNOWN) !== ChargerType.UNKNOWN &&
    chargingStatus &&
    [
      BatteryChargingStatus.PLUGGED,
      BatteryChargingStatus.IDLE,
      BatteryChargingStatus.CHARGING,
      BatteryChargingStatus.COMPLETED,
    ].includes(chargingStatus)
  );
};

/** Battery soc range thresholds - use this from v0.7 to get battery soc category ranges according to state of charge.*/
export const BATTERY_SOC_RANGES: {
  [k in BatterySocStatus]: {
    start?: number;
    end?: number;
    inclusiveStart?: boolean;
    inclusiveEnd?: boolean;
  };
} = {
  [BatterySocStatus.CRITICAL_CHARGE]: {
    end: 20,
    inclusiveEnd: true,
  },
  [BatterySocStatus.LOW_CHARGE]: {
    start: 21,
    end: 50,
    inclusiveStart: true,
    inclusiveEnd: true,
  },
  [BatterySocStatus.MEDIUM_CHARGE]: {
    start: 51,
    end: 80,
    inclusiveStart: true,
    inclusiveEnd: true,
  },
  [BatterySocStatus.HIGH_CHARGE]: {
    start: 81,
    inclusiveStart: true,
  },
};

/** Returns BatterySocStatus for given soc value according to Battery soc ranges */
export const getBatterySocStatus = (soc: number) => {
  for (const [status, range] of Object.entries(BATTERY_SOC_RANGES)) {
    const { start, end, inclusiveStart, inclusiveEnd } = range;

    const meetsStart =
      start === undefined || (inclusiveStart ? soc >= start : soc > start);
    const meetsEnd =
      end === undefined || (inclusiveEnd ? soc <= end : soc < end);

    if (meetsStart && meetsEnd) {
      return status as BatterySocStatus;
    }
  }
  return undefined;
};
